import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api, cardProduct } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";

// CMS-driven curated product rail: content = { eyebrow, product_ids: [...] }
export default function ProductShowcase({ data }) {
  const eyebrow = data?.eyebrow || "Featured";
  const ids = useMemo(() => data?.product_ids || [], [data]);
  const [items, setItems] = useState(null);
  const railRef = useRef(null);
  const { catalog, pincodeInfo } = useStore();
  const storeId = pincodeInfo?.store_id || "";

  const brandMap = useMemo(
    () => Object.fromEntries(catalog.brands.map((b) => [b.id, b.name])),
    [catalog.brands]
  );

  useEffect(() => {
    let cancelled = false;
    if (!ids.length) { setItems([]); return; }
    setItems(null);
    Promise.all(
      ids.map((id) =>
        api.get(`/ecom/catalog/products/${id}`, { params: { ...(storeId && { store_id: storeId }) } })
          .then((r) => r.data)
          .catch(() => null)
      )
    ).then((list) => {
      if (!cancelled) setItems(list.filter(Boolean)); // keeps CMS order
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(ids), storeId]);

  const scroll = (dir) => railRef.current?.scrollBy({ left: dir * 560, behavior: "smooth" });

  if (items && items.length === 0) return null; // all ids invalid — hide section

  return (
    <section data-testid="product-showcase-section" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold" data-testid="showcase-eyebrow">{eyebrow}</p>
            <h2 className="mt-3 max-w-xl font-display text-3xl leading-tight tracking-tight text-cream sm:text-5xl">
              The <span className="italic text-gold">{eyebrow.toLowerCase()}</span> edit
            </h2>
          </div>
          <div className="flex gap-2">
            <button data-testid="showcase-rail-prev-button" onClick={() => scroll(-1)} className="flex h-11 w-11 items-center justify-center rounded-full border border-champagne/50 text-cream transition-colors hover:border-gold hover:text-gold" aria-label="Scroll showcase left">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button data-testid="showcase-rail-next-button" onClick={() => scroll(1)} className="flex h-11 w-11 items-center justify-center rounded-full border border-champagne/50 text-cream transition-colors hover:border-gold hover:text-gold" aria-label="Scroll showcase right">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </Reveal>

      <div
        ref={railRef}
        data-testid="showcase-rail"
        className="mt-8 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4 no-scrollbar"
      >
        {items === null
          ? [...Array(5)].map((_, i) => <div key={i} className="aspect-[4/5] w-[220px] shrink-0 animate-pulse rounded-2xl bg-moss sm:w-[250px]" />)
          : items.map((p) => <ProductCard key={p.id} p={cardProduct(p, brandMap[p.brand_id])} />)}
      </div>

      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-fern" data-testid="showcase-count">
        {items === null ? "Loading…" : `${items.length} handpicked product${items.length === 1 ? "" : "s"}`} — curated by HQ
      </p>
    </section>
  );
}
